import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { formatInr, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { FileText, Plus, Ban, Upload, IndianRupee } from "lucide-react";
import {
    PAYMENT_METHODS,
    STATUS_TONE,
    TX_STATUSES,
    TX_TONE_CLASSES,
    TX_TYPES,
    fetchShopOwners,
    todayIso,
} from "./shared";

const TX_TYPE_MAP = Object.fromEntries(TX_TYPES.map((t) => [t.value, t]));

export default function TransactionsPage() {
    const [rows, setRows] = useState(null);
    const [total, setTotal] = useState(0);
    const [districts, setDistricts] = useState([]);
    const [filters, setFilters] = useState({
        type: "",
        status: "",
        merchant_user_id: "",
        district_slug: "",
        start: "",
        end: "",
        min_amount: "",
        max_amount: "",
    });
    const [openRecord, setOpenRecord] = useState(false);
    const [openImport, setOpenImport] = useState(false);

    const load = async () => {
        try {
            const params = new URLSearchParams({ limit: "200" });
            if (filters.type) params.set("type", filters.type);
            if (filters.status) params.set("status", filters.status);
            if (filters.district_slug) params.set("district_slug", filters.district_slug);
            if (filters.start) params.set("start", filters.start);
            if (filters.end) params.set("end", filters.end);
            if (filters.min_amount) params.set("min_amount_paise", Math.round(Number(filters.min_amount) * 100));
            if (filters.max_amount) params.set("max_amount_paise", Math.round(Number(filters.max_amount) * 100));
            const res = await api.get(`/admin/transactions?${params.toString()}`);
            setRows(res.data.transactions);
            setTotal(res.data.total);
        } catch (e) {
            toast.error("Could not load transactions");
        }
    };

    useEffect(() => {
        api.get("/districts").then((r) => setDistricts(r.data));
    }, []);
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

    const filteredTotal = useMemo(
        () => (rows || []).reduce((acc, r) => acc + Number(r.amount_paise || 0), 0),
        [rows],
    );

    return (
        <div className="mx-auto max-w-7xl px-6 py-8" data-testid="transactions-page">
            <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-orange-600">
                        Ledger
                    </div>
                    <h1 className="text-3xl font-extrabold text-neutral-900">Transactions</h1>
                    <p className="mt-1 text-sm text-neutral-500">
                        {total} rows · <b className="font-mono text-neutral-800">{formatInr(filteredTotal)}</b> filtered
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setOpenImport(true)} data-testid="bulk-import-btn">
                        <Upload size={14} className="mr-2" /> Bulk import
                    </Button>
                    <Button className="nest-gradient text-white" onClick={() => setOpenRecord(true)} data-testid="record-tx-btn">
                        <Plus size={16} className="mr-2" /> Record transaction
                    </Button>
                </div>
            </header>

            <Card className="mb-4 rounded-2xl border border-neutral-200 p-4">
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6" data-testid="tx-filters">
                    <FilterSelect
                        placeholder="Type"
                        value={filters.type}
                        onChange={(v) => setFilters((f) => ({ ...f, type: v }))}
                        options={[{ value: "__all__", label: "All types" }, ...TX_TYPES.map((t) => ({ value: t.value, label: t.label }))]}
                        testid="tx-filter-type"
                    />
                    <FilterSelect
                        placeholder="Status"
                        value={filters.status}
                        onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                        options={[{ value: "__all__", label: "All statuses" }, ...TX_STATUSES.map((s) => ({ value: s, label: s }))]}
                        testid="tx-filter-status"
                    />
                    <FilterSelect
                        placeholder="District"
                        value={filters.district_slug}
                        onChange={(v) => setFilters((f) => ({ ...f, district_slug: v }))}
                        options={[{ value: "__all__", label: "All districts" }, ...districts.map((d) => ({ value: d.slug, label: d.name }))]}
                        testid="tx-filter-district"
                    />
                    <Input type="date" value={filters.start} onChange={(e) => setFilters((f) => ({ ...f, start: e.target.value }))} data-testid="tx-filter-start" />
                    <Input type="date" value={filters.end} onChange={(e) => setFilters((f) => ({ ...f, end: e.target.value }))} data-testid="tx-filter-end" />
                    <div className="col-span-2 grid grid-cols-2 gap-2">
                        <Input type="number" placeholder="Min ₹" value={filters.min_amount} onChange={(e) => setFilters((f) => ({ ...f, min_amount: e.target.value }))} data-testid="tx-filter-min" />
                        <Input type="number" placeholder="Max ₹" value={filters.max_amount} onChange={(e) => setFilters((f) => ({ ...f, max_amount: e.target.value }))} data-testid="tx-filter-max" />
                    </div>
                </div>
            </Card>

            <Card className="rounded-2xl border border-neutral-200">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[900px] text-sm" data-testid="tx-table">
                        <thead>
                            <tr className="border-b border-neutral-200 bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-500">
                                <th className="px-4 py-3">Date</th>
                                <th className="px-4 py-3">Type</th>
                                <th className="px-4 py-3">District</th>
                                <th className="px-4 py-3 text-right">Amount</th>
                                <th className="px-4 py-3">Method</th>
                                <th className="px-4 py-3">Reference</th>
                                <th className="px-4 py-3">Description</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows === null &&
                                Array.from({ length: 6 }).map((_, i) => (
                                    <tr key={i}>
                                        {Array.from({ length: 9 }).map((_, j) => (
                                            <td key={j} className="px-4 py-3"><Skeleton className="h-4 w-full" /></td>
                                        ))}
                                    </tr>
                                ))}
                            {rows?.map((tx) => (
                                <TxRow key={tx.id} tx={tx} onChanged={load} />
                            ))}
                            {rows?.length === 0 && (
                                <tr>
                                    <td colSpan={9} className="px-4 py-10 text-center text-sm text-neutral-500">
                                        No transactions match your filters.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            <RecordTxDialog open={openRecord} onOpenChange={setOpenRecord} districts={districts} onCreated={() => { setOpenRecord(false); load(); }} />
            <BulkImportDialog open={openImport} onOpenChange={setOpenImport} onDone={() => { setOpenImport(false); load(); }} />
        </div>
    );
}

function FilterSelect({ value, onChange, options, placeholder, testid }) {
    return (
        <Select value={value || "__all__"} onValueChange={(v) => onChange(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid={testid}>
                <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
                {options.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

function TxRow({ tx, onChanged }) {
    const t = TX_TYPE_MAP[tx.type];
    const doVoid = async () => {
        try {
            await api.post(`/admin/transactions/${tx.id}/void`);
            toast.success("Voided");
            onChanged?.();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };
    return (
        <tr className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`tx-row-${tx.id}`}>
            <td className="px-4 py-3 text-xs text-neutral-500 whitespace-nowrap">{formatDate(tx.transaction_date)}</td>
            <td className="px-4 py-3">
                <Badge variant="outline" className={`text-[10px] ${TX_TONE_CLASSES[t?.tone || "slate"]}`}>{t?.label || tx.type}</Badge>
            </td>
            <td className="px-4 py-3 text-xs text-neutral-500">{tx.district_slug || <span className="italic text-neutral-400">org-level</span>}</td>
            <td className="px-4 py-3 text-right font-mono text-neutral-800">{formatInr(tx.amount_paise)}</td>
            <td className="px-4 py-3 text-xs text-neutral-500">{tx.payment_method || "—"}</td>
            <td className="px-4 py-3 font-mono text-xs text-neutral-500">{tx.payment_reference || "—"}</td>
            <td className="px-4 py-3 text-xs text-neutral-700 max-w-xs truncate" title={tx.description}>{tx.description}</td>
            <td className="px-4 py-3">
                <Badge variant="outline" className={STATUS_TONE[tx.status]}>{tx.status}</Badge>
            </td>
            <td className="px-4 py-3 text-right">
                {tx.status !== "VOID" && (
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50" data-testid={`tx-void-${tx.id}`}>
                                <Ban size={14} className="mr-1" /> Void
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Void this transaction?</AlertDialogTitle>
                                <AlertDialogDescription>The row remains for audit. Reports will exclude it.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Back</AlertDialogCancel>
                                <AlertDialogAction onClick={doVoid}>Void</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                )}
            </td>
        </tr>
    );
}

function RecordTxDialog({ open, onOpenChange, districts, onCreated }) {
    const [type, setType] = useState("MOVIE_COMMISSION");
    const [amountRupees, setAmountRupees] = useState("");
    const [merchants, setMerchants] = useState([]);
    const [merchantId, setMerchantId] = useState("");
    const [districtSlug, setDistrictSlug] = useState("");
    const [txDate, setTxDate] = useState(todayIso());
    const [payMethod, setPayMethod] = useState("BANK_TRANSFER");
    const [payRef, setPayRef] = useState("");
    const [description, setDescription] = useState("");
    const [metadata, setMetadata] = useState("");
    const [showMeta, setShowMeta] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) fetchShopOwners().then(setMerchants).catch(() => {});
    }, [open]);

    const submit = async () => {
        const paise = Math.round(Number(amountRupees) * 100);
        if (!paise || paise <= 0) {
            toast.error("Enter a valid amount in ₹.");
            return;
        }
        if (!description.trim()) {
            toast.error("Description is required.");
            return;
        }
        let meta = {};
        if (metadata.trim()) {
            try { meta = JSON.parse(metadata); }
            catch { toast.error("Metadata must be valid JSON"); return; }
        }
        try {
            setBusy(true);
            await api.post("/admin/transactions", {
                type,
                amount_paise: paise,
                merchant_user_id: merchantId || null,
                district_slug: districtSlug || null,
                transaction_date: txDate,
                payment_method: payMethod,
                payment_reference: payRef || null,
                description,
                metadata: meta,
                status: "COMPLETED",
            });
            toast.success("Transaction recorded");
            setAmountRupees(""); setDescription(""); setPayRef(""); setMetadata("");
            onCreated?.();
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="record-tx-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><FileText size={16} className="text-orange-600" /> Record transaction</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs">Type *</Label>
                        <Select value={type} onValueChange={setType}>
                            <SelectTrigger data-testid="rec-tx-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {TX_TYPES.map((t) => (
                                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">Amount (₹) *</Label>
                        <div className="relative">
                            <IndianRupee size={14} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
                            <Input className="pl-7" type="number" min={1} value={amountRupees} onChange={(e) => setAmountRupees(e.target.value)} data-testid="rec-tx-amount" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Merchant (optional)</Label>
                        <Select value={merchantId || "__none__"} onValueChange={(v) => setMerchantId(v === "__none__" ? "" : v)}>
                            <SelectTrigger data-testid="rec-tx-merchant"><SelectValue placeholder="Org-level" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__none__">Org-level (no merchant)</SelectItem>
                                {merchants.slice(0, 50).map((m) => (
                                    <SelectItem key={m.id} value={m.id}>{m.business_name || m.full_name} · {m.mobile_number || m.email}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">District</Label>
                        <Select value={districtSlug || "__none__"} onValueChange={(v) => setDistrictSlug(v === "__none__" ? "" : v)}>
                            <SelectTrigger data-testid="rec-tx-district"><SelectValue placeholder="Auto-fill from merchant" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__none__">Auto / None</SelectItem>
                                {districts.map((d) => (
                                    <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">Transaction date *</Label>
                        <Input type="date" value={txDate} onChange={(e) => setTxDate(e.target.value)} data-testid="rec-tx-date" />
                    </div>
                    <div>
                        <Label className="text-xs">Payment method</Label>
                        <Select value={payMethod} onValueChange={setPayMethod}>
                            <SelectTrigger data-testid="rec-tx-method"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {PAYMENT_METHODS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="col-span-2">
                        <Label className="text-xs">Payment reference</Label>
                        <Input value={payRef} onChange={(e) => setPayRef(e.target.value)} placeholder="UPI txn / bank ref" data-testid="rec-tx-ref" />
                    </div>
                    <div className="col-span-2">
                        <Label className="text-xs">Description *</Label>
                        <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="rec-tx-desc" />
                    </div>
                    <div className="col-span-2">
                        <button type="button" onClick={() => setShowMeta(!showMeta)} className="text-xs font-semibold text-orange-600" data-testid="rec-tx-toggle-meta">
                            {showMeta ? "− Hide" : "+ Show"} advanced metadata (JSON)
                        </button>
                        {showMeta && (
                            <Textarea rows={3} className="mt-2 font-mono" value={metadata} onChange={(e) => setMetadata(e.target.value)} placeholder='{"custom_field":"value"}' data-testid="rec-tx-meta" />
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button className="nest-gradient text-white" disabled={busy} onClick={submit} data-testid="rec-tx-submit">
                        {busy ? "Recording…" : "Record"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function BulkImportDialog({ open, onOpenChange, onDone }) {
    const [json, setJson] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const submit = async () => {
        try {
            const rows = JSON.parse(json);
            if (!Array.isArray(rows)) throw new Error("Payload must be a JSON array");
            setBusy(true);
            const res = await api.post("/admin/transactions/bulk-import", { rows });
            setResult(res.data);
            toast.success(`Imported ${res.data.ok}/${res.data.total}`);
        } catch (e) {
            toast.error(errorMessage(e) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const sample = JSON.stringify(
        [
            {
                type: "MOVIE_COMMISSION",
                amount_paise: 500000,
                district_slug: "ernakulam",
                transaction_date: todayIso(),
                description: "Sample import row 1",
                payment_method: "BANK_TRANSFER",
            },
        ],
        null,
        2,
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="bulk-import-dialog">
                <DialogHeader>
                    <DialogTitle>Bulk import transactions</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <p className="text-xs text-neutral-500">Paste a JSON array of transaction rows. Each row uses the same fields as the record dialog.</p>
                    <Textarea rows={10} className="font-mono text-xs" value={json} onChange={(e) => setJson(e.target.value)} placeholder={sample} data-testid="bulk-import-json" />
                    {result && (
                        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 text-xs" data-testid="bulk-import-result">
                            <div className="mb-1 font-semibold">Result: {result.ok} / {result.total} imported</div>
                            <ul className="space-y-1">
                                {result.results.slice(0, 20).map((r, i) => (
                                    <li key={i} className={r.ok ? "text-emerald-700" : "text-rose-700"}>
                                        Row {r.row + 1}: {r.ok ? `OK (id ${r.id?.slice(0, 8)}…)` : r.error}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onDone}>Close</Button>
                    <Button className="nest-gradient text-white" disabled={busy || !json} onClick={submit} data-testid="bulk-import-submit">
                        {busy ? "Importing…" : "Import"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
